import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { fmtDate, fmtTime } from '../lib/format.js';
import { ContactRow } from './ContactRow.jsx';
import { InterviewScriptPanel } from './InterviewScriptPanel.jsx';

// ApplicationDetail — the hiring-pipeline application review pane. Extracted
// from app.jsx (decomposition). APPLY_SCENARIOS + RATING_BTN_STYLE are used
// only here, so they stay module-local.
// Canonical scenario prompts mirroring public/apply.html step 7. Surfaced
// in the reviewer view so managers can see what the candidate was
// answering without bouncing to the apply page (CJ 2026-05-16).
//
// `axis` ties the scenario to the rubric axis it primarily tests, so the
// inline strong/ok/weak rating on a scenario writes directly to the
// `application_review_axes` row for that axis. CJ 2026-05-16 wanted
// scoring folded into the per-question content rather than living in a
// separate axis grid at the bottom.
const APPLY_SCENARIOS = {
  1: {
    title: 'A slow Tuesday',
    axis: 'awareness',
    prompt: "It's 2:30pm on a Tuesday. The bar is wiped, the espresso machine is clean, and there are no customers in the café. You have about 15 minutes before the next rush. What do you actually do?",
    why: "Awareness + initiative. Strong answers notice something specific (restocking pastries, topping the bean hopper, refreshing milk, wiping menus, prepping limes, or asking a coworker if they need anything). It's a window into how they read a quiet room.",
  },
  2: {
    title: "A first-time customer can't decide",
    axis: 'hospitality',
    prompt: 'A customer steps up to the register, looks at the menu for a while, then looks back at you and says, "I\'ve never been in here before, what\'s good?" Two other customers in line behind them, not in a rush. What do you actually do?',
    why: "Hospitality + handling more than one person at once. Strong answers ask one clarifying question (hot or iced, sweet or not), make a real recommendation, and acknowledge the people in line. Not whether they nail a specific drink.",
  },
  3: {
    title: "A coworker isn't pulling their weight",
    axis: 'collaboration',
    prompt: "You've worked four shifts with a coworker who started two weeks after you. They're polite, on time, but they keep skipping cleaning tasks, forgetting closing steps, and leaving the heavier work to you. You don't know if they're struggling or just not invested. What do you actually do?",
    why: "Empathy + initiative without aggression. Strong answers talk to the coworker directly and gently, then loop in the manager if nothing changes. Quietly doing everything alone (martyrdom) isn't a strong answer.",
  },
  4: {
    title: "You don't know the answer",
    axis: 'learning_posture',
    prompt: 'A customer asks a specific coffee question — say, the difference between a washed and natural Ethiopian, or what makes our house blend "house." You genuinely don\'t know the answer. There\'s a line of three behind them. What do you actually do?',
    why: "Honesty + composure under pressure. Strong answers admit they don't know without freezing, offer to find out or check with a coworker, and keep the line moving. Looking for grace, not for knowing every origin spec.",
  },
};

const RATING_BTN_STYLE = (r, selected) => ({
  fontSize: 11, padding: '4px 10px', borderRadius: 6, fontWeight: 600,
  border: '1px solid', cursor: 'pointer',
  background: selected ? (r === 'strong' ? '#dcfce7' : r === 'ok' ? '#fef3c7' : '#fee2e2') : '#fff',
  borderColor: selected ? (r === 'strong' ? '#86efac' : r === 'ok' ? '#fcd34d' : '#fca5a5') : '#d4d4d4',
  color: r === 'strong' ? '#166534' : r === 'ok' ? '#92400e' : '#991b1b',
});

// ContactRow (editable contact line) lives in src/components/ContactRow.jsx.

// Claim-or-Pass (CJ 2026-07-29). A "pass" is a per-manager signal, not a decline.
const PASS_REASONS = [
  { value: 'dont_need', label: "Don't need" },
  { value: 'poor_availability', label: 'Poor availability' },
  { value: 'not_a_fit', label: 'Not a fit' },
  { value: 'role_filled', label: 'Role already filled' },
  { value: 'commute', label: 'Outside commute area' },
  { value: 'other', label: 'Other' },
];
const PASS_REASON_LABEL = Object.fromEntries(PASS_REASONS.map((r) => [r.value, r.label]));

// Per-manager pass control: shows who has passed (and why), and lets the current
// manager pass with a reason or withdraw their pass. Self-contained state.
function PassControl({ passes, currentUser, onPass, onUnpass }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('dont_need');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const list = Array.isArray(passes) ? passes : [];
  const mine = currentUser ? list.find((p) => Number(p.user_id) === Number(currentUser.id)) : null;

  async function submit() {
    setBusy(true);
    try { await onPass({ reason, note: note.trim() || null }); setOpen(false); setNote(''); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: 8, padding: '8px 12px', background: '#fafafa', border: '1px solid #e0e0e0',
                  borderRadius: 6, fontSize: 12 }}>
      {list.length > 0 ? (
        <div style={{ marginBottom: mine ? 6 : 8 }}>
          <span style={{ color: '#666', fontWeight: 600 }}>Passed by:</span>{' '}
          {list.map((p, i) => (
            <span key={p.user_id} title={p.note || ''}>
              {i > 0 ? ', ' : ''}{(p.user_name || 'A manager').split(' ')[0]}
              <span style={{ color: '#999' }}> ({PASS_REASON_LABEL[p.reason] || p.reason})</span>
            </span>
          ))}
        </div>
      ) : (
        <div style={{ color: '#666', marginBottom: 8 }}>No manager has passed on this candidate.</div>
      )}
      {mine ? (
        <button onClick={() => !busy && onUnpass()} disabled={busy}
          style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #d0d4d9', background: '#fff',
                    color: '#555', borderRadius: 6, cursor: 'pointer' }}>
          You passed ({PASS_REASON_LABEL[mine.reason] || mine.reason}) · Withdraw
        </button>
      ) : !open ? (
        <button onClick={() => setOpen(true)}
          style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #d0d4d9', background: '#fff',
                    color: '#8a5a1f', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
          Pass on this one
        </button>
      ) : (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={reason} onChange={(e) => setReason(e.target.value)}
            style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #d0d4d9', borderRadius: 6, fontFamily: 'inherit' }}>
            {PASS_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {reason === 'other' && (
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (optional note)"
              style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #d0d4d9', borderRadius: 6, fontFamily: 'inherit', minWidth: 160 }} />
          )}
          <button onClick={submit} disabled={busy}
            style={{ fontSize: 11, padding: '5px 12px', border: 'none', background: 'var(--forest, #1f4d3a)',
                      color: '#fff', borderRadius: 6, cursor: busy ? 'default' : 'pointer', fontWeight: 600 }}>
            {busy ? '…' : 'Confirm pass'}
          </button>
          <button onClick={() => setOpen(false)} style={{ fontSize: 11, padding: '5px 8px', border: 'none', background: 'transparent', color: '#999', cursor: 'pointer' }}>Cancel</button>
        </div>
      )}
    </div>
  );
}

export function ApplicationDetail({ detail, onPostReview, onBlock, onDelete, onHire, onClaim, onUnclaim, onPass, onUnpass, onArchive, onUnarchive, onEditContact, onUnhire, locations, currentUser, savingMsg }) {
  const a = detail.application;
  const reviews = detail.reviews || [];
  // CJ 2026-05-26: managers run interviews and hire candidates day-to-day,
  // so the gate here is "manager+" rather than owner-only. Backend hire
  // + send-onboarding-bundle endpoints already require view_admin_panel
  // which covers manager, so this is a UI-parity change only.
  const canHire = currentUser && window.EmpireRoles.isManager(currentUser);
  const alreadyHired = a.status === 'hired';
  // Surface the most recent decision-bearing review at the top.
  const latestDecisionReview = [...reviews].reverse().find(r => r.decision);
  const [hireOpen, setHireOpen] = useState(false);
  const [hireSaving, setHireSaving] = useState(false);
  const defaultLoc = (a.preferred_cafe_id || a.cafe_id || (Array.isArray(a.cafe_ids) && a.cafe_ids[0]) || (locations && locations[0]?.id) || null);
  const [hireForm, setHireForm] = useState({
    primary_location_id: defaultLoc || '',
    additional_location_ids: [],
    role: 'Barista',
    hourly_rate: '17',
    pay_entity: 'ETC',
    send_welcome: true,
    first_shift_date: '',
  });
  // Sync primary_location_id when locations finish loading after the modal
  // opened with an empty default. Without this the dropdown sits on
  // "— pick —" forever even though we computed a defaultLoc later.
  useEffect(() => {
    if (!hireForm.primary_location_id && defaultLoc) {
      setHireForm(f => ({ ...f, primary_location_id: defaultLoc }));
    }
  }, [defaultLoc]);
  const hireDisabled = hireSaving
    || !Number.isFinite(Number(hireForm.primary_location_id))
    || Number(hireForm.primary_location_id) <= 0
    || !(Number(hireForm.hourly_rate) > 0);
  async function submitHire() {
    if (hireDisabled) return;
    setHireSaving(true);
    try {
      await onHire({
        primary_location_id: Number(hireForm.primary_location_id),
        additional_location_ids: hireForm.additional_location_ids.map(Number),
        role: hireForm.role,
        hourly_rate: Number(hireForm.hourly_rate),
        pay_entity: hireForm.pay_entity,
        send_welcome: !!hireForm.send_welcome,
        first_shift_date: hireForm.first_shift_date || null,
      });
      setHireOpen(false);
    } catch (e) { /* alert in parent */ }
    finally { setHireSaving(false); }
  }
  const [comment, setComment] = useState('');
  const [decision, setDecision] = useState('');
  const [decisionReason, setDecisionReason] = useState('');
  // Hiring v3 / Ticket C3 (extended 2026-05-08) — Decision email system.
  // CJ feedback: invite-to-interview and phone-screen need to actually
  // *do* something (send the candidate). Pass needs pre-filled variants
  // so managers actually finish the workflow. Each decision now has
  // multiple template variants; manager picks one and edits.
  //
  // Defaults:
  //   invite_to_interview → email default ON (the "doing something")
  //   phone_screen_set    → email default ON, with optional virtual link
  //   pass                → default OFF (privacy-first per RI counsel)
  const [emailSend, setEmailSend] = useState(false);
  const [emailVariant, setEmailVariant] = useState('default');
  const [emailBody, setEmailBody] = useState('');
  const [meetingLink, setMeetingLink] = useState('');
  const greetingName = (() => {
    const p = (a.preferred_name || '').trim();
    const f = (a.first_name || '').trim();
    return p || f || 'there';
  })();

  // CJ 2026-05-16: the reviewing manager's Google Appointment Schedule
  // booking URL, if set on their profile. When present we auto-inject a
  // "Pick a time" line into the invite-to-interview emails; otherwise
  // the template falls back to the old reply-with-times language.
  // We fetch it from /api/profile/me on mount rather than relying on the
  // cached currentUser, which may pre-date the column existing.
  const [myApptUrl, setMyApptUrl] = useState(currentUser?.google_appointment_url || '');
  useEffect(() => {
    if (!currentUser?.id) return;
    api('GET', `/api/profile/${currentUser.id}`, null, { 'x-user-id': String(currentUser.id) })
      .then(r => setMyApptUrl((r?.profile?.google_appointment_url || '').trim()))
      .catch(() => {});
  }, [currentUser?.id]);

  // CJ 2026-06-15: rejection (pass) email templates moved into the DB so
  // managers + owners can edit / add without a code change. Fetched once
  // per modal mount; falls back to the hardcoded set below if the API
  // returns nothing or the network blips. Other decisions (invite/phone
  // screen/hold) still use the hardcoded variants — only pass was
  // explicitly requested for editing.
  const [dbPassTemplates, setDbPassTemplates] = useState(null);
  useEffect(() => {
    api('GET', '/api/admin/application-email-templates?kind=pass', null,
        { 'x-user-id': String(currentUser.id) })
      .then(r => setDbPassTemplates(Array.isArray(r?.templates) ? r.templates : []))
      .catch(() => setDbPassTemplates([]));
  }, [currentUser?.id]);
  // Render {{first_name}} / {{business_name}} placeholders in a DB template
  // body. Server-side renderTemplate is the source of truth for sends; this
  // is just the client-side preview the manager edits before save.
  function renderDbTemplateBody(body) {
    const businessName = 'Empire Tea & Coffee';
    return String(body || '')
      .replace(/\{\{first_name\}\}/g, greetingName)
      .replace(/\{\{business_name\}\}/g, businessName);
  }
  const apptUrl = (myApptUrl || '').trim();
  const apptLine = apptUrl
    ? `Pick a time that works for you here: ${apptUrl}`
    : `Reply to this email or call us with a few times that work, and we'll find one that fits both schedules.`;
  const apptLineEnthusiastic = apptUrl
    ? `Pick a time that works for you here: ${apptUrl}`
    : `Could you reply with two or three times that work for a 30-minute conversation in the next week?`;

  // Templates keyed by decision + variant. {{name}} is interpolated to
  // preferred name (or first name) on the client; manager can still edit.
  const DECISION_EMAIL_TEMPLATES = {
    invite_to_interview: {
      default: {
        label: 'In-person interview',
        body: `Hi ${greetingName},

Thanks for the time you put into your application and we would love to talk further.

We'd like to invite you in for a 30-minute conversation at one of the cafés you applied to. ${apptLine}

Looking forward to meeting you,
The Empire Team`,
      },
      enthusiastic: {
        label: 'Enthusiastic invite',
        body: `Hi ${greetingName},

Your application stood out and we appreciate the time you took. We'd love to meet you in person.

${apptLineEnthusiastic} We can meet at the café you applied to so you also get to see the bar in motion.

Looking forward,
The Empire Team`,
      },
    },
    phone_screen_set: {
      phone: {
        label: 'Phone screen',
        body: `Hi ${greetingName},

Thanks for your application. We'd like to set up a quick 15–20 minute phone call as a first step.

Reply with a couple of windows that work for you in the next few days, and we'll lock one in.

Talk soon,
The Empire Team`,
      },
      virtual: {
        label: 'Virtual screen (Meet / Zoom)',
        body: `Hi ${greetingName},

Thanks for your application. We'd like to set up a brief 20-minute video conversation as a first step.

We've set up a meeting link below. Reply with a window that works in the next few days and we'll confirm the time.

Meeting link: {{meeting_link}}

Talk soon,
The Empire Team`,
      },
    },
    pass: {
      availability: {
        label: 'Availability mismatch',
        body: `Hi ${greetingName},

Thank you for your interest in joining the team at Empire Tea & Coffee. After careful review, we're not able to move forward at this time. Your availability didn't quite align with what we have open.

If your schedule shifts in the next few months, you're welcome to reapply. We appreciate the time you put into applying.

Warmly,
The Empire Team`,
      },
      position_filled: {
        label: 'Position filled',
        body: `Hi ${greetingName},

Thank you for applying to Empire Tea & Coffee. We were lucky to receive a number of strong applications and have already filled the role you applied for.

We'd love for you to reapply when we post the next opening. Thank you again for the time you took.

Warmly,
The Empire Team`,
      },
      experience: {
        label: 'Looking for more experience',
        body: `Hi ${greetingName},

Thank you for your application to Empire Tea & Coffee. We're looking for someone with a bit more experience for this particular role, but we appreciated your interest.

Please feel free to reapply for future openings. If you build experience elsewhere in the meantime, we'd be glad to take another look.

Warmly,
The Empire Team`,
      },
      other: {
        label: 'Generic',
        body: `Hi ${greetingName},

Thank you for your interest in joining the team at Empire Tea & Coffee. After careful review, we won't be moving forward with your application at this time.

We appreciate the time you put into applying and wish you the best in your search.

Warmly,
The Empire Team`,
      },
    },
  };

  // Effective template set for a decision. Pass uses DB-fetched templates
  // when present, falling back to the hardcoded ones if the API returned
  // an empty list (or hasn't loaded yet). Shape matches the hardcoded
  // variants: { key: { label, body } } so the picker UI doesn't care
  // about the source.
  function templatesFor(dec) {
    if (dec === 'pass' && Array.isArray(dbPassTemplates) && dbPassTemplates.length > 0) {
      const out = {};
      for (const t of dbPassTemplates) {
        out[`db-${t.id}`] = {
          label: t.name || 'Untitled',
          body: renderDbTemplateBody(t.body),
        };
      }
      return out;
    }
    return DECISION_EMAIL_TEMPLATES[dec] || null;
  }

  function applyEmailTemplate(dec, variant) {
    const set = templatesFor(dec);
    if (!set) return;
    const tpl = set[variant] || Object.values(set)[0];
    if (!tpl) return;
    setEmailBody(tpl.body);
  }
  // When decision changes: reset send-state per default policy + seed
  // body with first variant. Pass templates come from the DB so we also
  // re-run when those finish loading (managers can add/remove templates
  // in Settings and we pick the change up on next modal open).
  useEffect(() => {
    const set = templatesFor(decision);
    if (!decision || !set) {
      setEmailSend(false);
      setEmailBody('');
      setEmailVariant('default');
      return;
    }
    const variants = Object.keys(set);
    const firstVariant = variants[0];
    setEmailVariant(firstVariant);
    applyEmailTemplate(decision, firstVariant);
    // Pass defaults OFF; invite + phone screen default ON so they "do something".
    setEmailSend(decision !== 'pass');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decision, dbPassTemplates]);
  // Hiring v3 / Ticket C1 — 7-axis rubric. Each axis has a rating (Strong /
  // OK / Weak / null = "Not enough info") AND an optional per-axis comment.
  // Axes shape: { axis_key: { rating: 'strong'|'ok'|'weak'|null, comment: string } }
  const [axes, setAxes] = useState({});
  function setAxisRating(axis, rating) {
    setAxes((a) => ({ ...a, [axis]: { ...(a[axis] || {}), rating } }));
  }
  function setAxisComment(axis, comment) {
    setAxes((a) => ({ ...a, [axis]: { ...(a[axis] || {}), comment } }));
  }

  // Spec §10 — 7 axes, in the order they should render. Awareness first
  // (matches the candidate's first scenario); Logistics Realism last
  // (operational rather than behavioral).
  const RUBRIC = [
    ['awareness', 'Awareness', 'Notices what needs doing without being told.'],
    ['hospitality', 'Hospitality', 'Warmth toward customers AND coworkers.'],
    ['collaboration', 'Collaboration', 'Works with the team, not around them.'],
    ['learning_posture', 'Learning posture', 'Coachable. Open to correction.'],
    ['calm_under_pressure', 'Calm under pressure', "Composure in rush, conflict, or surprise."],
    ['communication_quality', 'Communication quality', 'Clear, specific, thoughtful.'],
    ['logistics_realism', 'Logistics realism', 'Availability + transport match the role.'],
  ];

  const RATINGS = ['strong', 'ok', 'weak'];
  // Spec §11 — 8 decision states. "Hire" lives on the Hire button (separate
  // flow); the four below are the disposition picks on this surface. Phone
  // screen now covers virtual interviews per CJ feedback 2026-05-08.
  const DECISIONS = [
    ['invite_to_interview', 'Invite to interview'],
    ['phone_screen_set', 'Phone or virtual screen'],
    ['hold_for_future', 'Hold for future'],
    ['pass', 'Pass'],
  ];

  async function submit() {
    const payload = {};
    if (comment.trim()) payload.comment = comment.trim();
    if (decision) {
      payload.decision = decision;
      if (decisionReason.trim()) payload.decision_reason = decisionReason.trim();
    }
    // Send the new {axes: {...}} shape. Server (post A1) accepts both this
    // and the legacy flat shape; new clients use the new one.
    const cleanAxes = {};
    let hasAxisRating = false;
    for (const [axis, val] of Object.entries(axes)) {
      if (val && (val.rating || (val.comment && val.comment.trim()))) {
        cleanAxes[axis] = {};
        if (val.rating) { cleanAxes[axis].rating = val.rating; hasAxisRating = true; }
        if (val.comment && val.comment.trim()) cleanAxes[axis].comment = val.comment.trim();
      }
    }
    if (Object.keys(cleanAxes).length > 0) payload.axes = cleanAxes;
    // Server requires at least one of: comment, decision, axis rating.
    // (Axis notes without a rating don't count toward "anything to save?".)
    // CJ 2026-05-20: surface a friendly inline alert instead of letting
    // the server's "Provide a comment, rating, or decision" pop in an
    // alert dialog.
    if (!payload.comment && !payload.decision && !hasAxisRating) {
      alert('Pick a disposition (Invite / Phone screen / Hold / Pass), rate at least one scenario, or add a comment before saving.');
      return;
    }

    // Hiring v3 / C3 (extended) — Decision email payload. Server fires
    // the email when decision_email_sent=true. Pass uses pass_email_sent
    // for back-compat; invite / phone-screen use the new key.
    if (decision && emailSend && templatesFor(decision)) {
      let body = emailBody.trim();
      if (decision === 'phone_screen_set' && emailVariant === 'virtual') {
        body = body.replace('{{meeting_link}}', meetingLink.trim() || '(link to follow)');
      }
      if (decision === 'pass') {
        payload.pass_email_sent = true;
        if (body) payload.pass_email_body = body;
      } else {
        payload.decision_email_sent = true;
        if (body) payload.decision_email_body = body;
      }
    }

    await onPostReview(payload);
    setComment(''); setDecision(''); setDecisionReason(''); setAxes({});
    setEmailSend(false); setEmailBody(''); setEmailVariant('default'); setMeetingLink('');
  }

  // CJ 2026-05-21: scheduling an interview shouldn't require any rubric
  // ratings or post-interview decision — that all comes AFTER the
  // candidate is interviewed. One-click invite that bypasses the rubric
  // gate entirely. The "decision" field still flips to invite_to_interview
  // server-side so the application moves to interview_scheduled, but the
  // manager doesn't have to think of it as a hiring decision.
  async function sendInviteToInterview() {
    const inviteBody = `Hi ${greetingName},

Thanks for the time you put into your application and we would love to talk further.

We'd like to invite you in for a 30-minute conversation at one of the cafés you applied to. ${apptLine}

Looking forward to meeting you,
The Empire Team`;
    const ok = window.confirm(
      `Send the interview invite to ${a.first_name} ${a.last_name} (${a.email}) now?\n\n` +
      `${apptUrl
        ? `Includes your booking link: ${apptUrl}`
        : `Heads-up: you haven't set your Google Appointment URL in your profile, so the email asks them to reply with times. Cancel and set the URL if you want a self-serve link.`}`
    );
    if (!ok) return;
    try {
      await onPostReview({
        decision: 'invite_to_interview',
        decision_email_sent: true,
        decision_email_body: inviteBody,
      });
      setComment(''); setDecision(''); setDecisionReason(''); setAxes({});
      setEmailSend(false); setEmailBody(''); setEmailVariant('default'); setMeetingLink('');
    } catch (_) { /* onPostReview already alerts */ }
  }

  function fmtAvail(av) {
    if (!av || typeof av !== 'object') return '—';
    const days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
    function fmtTime(t) {
      // Convert HH:MM to "8am" / "1:30pm" style.
      if (!/^\d{2}:\d{2}$/.test(t)) return t;
      const [hh, mm] = t.split(':').map(Number);
      const ampm = hh < 12 ? 'am' : 'pm';
      const h12 = ((hh + 11) % 12) + 1;
      return mm === 0 ? `${h12}${ampm}` : `${h12}:${String(mm).padStart(2, '0')}${ampm}`;
    }
    return days.map(d => {
      const entries = av[d];
      if (!Array.isArray(entries) || entries.length === 0) return `${d.slice(0,3).toUpperCase()}: —`;
      const ranges = entries.map(e => {
        if (typeof e === 'string') return e;
        return `${fmtTime(e.start)}–${fmtTime(e.end)}`;
      }).join(' & ');
      return `${d.slice(0,3).toUpperCase()}: ${ranges}`;
    }).join(' · ');
  }

  // v1.6 multi-cafe label.
  let cafeLabel;
  if (a.cafe_flexible) {
    cafeLabel = 'Flexible — any of our locations';
  } else if (Array.isArray(a.cafes) && a.cafes.length > 0) {
    const preferredId = a.preferred_cafe_id;
    const names = a.cafes.map(c => c.id === preferredId ? `${c.name} (preferred)` : c.name);
    cafeLabel = names.join(', ');
  } else {
    cafeLabel = a.cafe_name || '—';
  }

  // Tenure rendering — v1.6 prefers tenure_kind + dates over the legacy band.
  function fmtDate(d) {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return String(d); }
  }
  let tenureLabel = a.intended_tenure || '—';
  if (a.tenure_kind === 'seasonal_summer') {
    tenureLabel = `Summer/seasonal — ${fmtDate(a.season_start_date)} → ${fmtDate(a.season_end_date)}`;
  } else if (a.tenure_kind === 'school_year') {
    tenureLabel = `School year — through ${fmtDate(a.school_year_end_date)}`;
  } else if (a.tenure_kind === 'permanent') {
    tenureLabel = 'Year-round, ongoing';
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#212121' }}>
            {a.first_name} {a.last_name}
            {a.preferred_name && a.preferred_name.trim() && a.preferred_name.trim().toLowerCase() !== a.first_name.toLowerCase() && (
              <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 400, color: '#666' }}>
                (goes by <strong>{a.preferred_name}</strong>)
              </span>
            )}
          </h2>
          <ContactRow application={a} onSave={onEditContact} />
          <div style={{ fontSize: 13, color: '#666', marginTop: 6 }}>
            <strong>Café:</strong> {cafeLabel}
          </div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            Visited: {a.have_visited_cafe || '—'}
            {' · '}
            Zip: {a.zip_code}
            {' · '}
            Age: {a.age_band}
          </div>
          {/* Hiring v3 / E7 + I2 — surface role_interests + source_of_hire
              in the detail header so managers see what the candidate
              picked + how they found us without scrolling. */}
          {Array.isArray(a.role_interests) && a.role_interests.length > 0 && (
            <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
              <strong>Roles of interest:</strong>{' '}
              {a.role_interests.map(r => (
                <span key={r} style={{ background: '#fef9ee', border: '1px solid #f5e6c8',
                                        borderRadius: 4, padding: '1px 6px', marginRight: 4, fontSize: 11 }}>
                  {r}
                </span>
              ))}
            </div>
          )}
          {a.source_of_hire && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              Source: <strong>{a.source_of_hire.replace(/_/g, ' ')}</strong>
              {a.referrer_name && <> · referred by <strong>{a.referrer_name}</strong></>}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
          <button onClick={onBlock}
            style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #fecaca', color: '#a13030',
                      background: '#fef2f2', borderRadius: 6, cursor: 'pointer' }}>
            Block applicant
          </button>
          {onDelete && (
            <button onClick={onDelete}
              title="Soft-delete — hides from list, review history preserved for audit"
              style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #b00020', color: '#fff',
                        background: '#b00020', borderRadius: 6, cursor: 'pointer' }}>
              Delete application
            </button>
          )}
        </div>
      </div>

      {/* Last reviewer / decision banner — surfaced prominently so you don't
          have to scroll. Each review row in Activity below also shows its
          own reviewer name + timestamp. */}
      {latestDecisionReview && (
        <div style={{ marginTop: 10, padding: '8px 12px', background: '#fef9ee',
                      border: '1px solid #f5e6c8', borderRadius: 6, fontSize: 12,
                      display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <span>
            Latest decision: <strong>{latestDecisionReview.decision.replace(/_/g, ' ')}</strong>
            {' '}by <strong>{latestDecisionReview.reviewer_name || 'Manager'}</strong>
          </span>
          <span style={{ color: '#888' }}>
            {new Date(latestDecisionReview.created_at).toLocaleString()}
          </span>
        </div>
      )}

      {/* Hiring v3 / Ticket B4 — Non-exclusive claim affordance.
          Coordination signal across cafés, not a lock. Three states:
          - unclaimed → "Claim this — non-exclusive" (cream)
          - claimed by me → "You're leading this · Release"
          - claimed by someone else → "Sarah is leading this · Take over" */}
      {(() => {
        const claimedByMe = a.claimed_by_user_id && currentUser
          && Number(a.claimed_by_user_id) === Number(currentUser.id);
        const claimedByOther = a.claimed_by_user_id && !claimedByMe;
        const claimerName = a.claimed_by_name || 'Another manager';
        return (
          <div style={{ marginTop: 10, padding: '8px 12px',
                        background: claimedByMe ? '#f0f7f0' : claimedByOther ? '#fff8e6' : '#fafafa',
                        border: `1px solid ${claimedByMe ? '#c8e2c8' : claimedByOther ? '#f0d9a3' : '#e0e0e0'}`,
                        borderRadius: 6, fontSize: 12,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span>
              {claimedByMe && <><strong>You're leading this</strong> — visible to other managers in the activity bar.</>}
              {claimedByOther && <><strong>{claimerName}</strong> is leading this. Non-exclusive — anyone can take over.</>}
              {!a.claimed_by_user_id && <span style={{ color: '#666' }}>No one's leading this yet.</span>}
            </span>
            <span style={{ display: 'flex', gap: 6 }}>
              {claimedByMe && (
                <button onClick={onUnclaim}
                  style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #c8e2c8',
                            background: '#fff', color: '#2c5b2c', borderRadius: 6, cursor: 'pointer' }}>
                  Release
                </button>
              )}
              {!claimedByMe && (
                <button onClick={onClaim}
                  style={{ fontSize: 11, padding: '4px 10px', border: '1px solid var(--gold, #c89b3c)',
                            background: 'var(--cream, #fef9ee)', color: '#212121', borderRadius: 6, cursor: 'pointer' }}>
                  {claimedByOther ? 'Take over' : 'Claim — I’ll lead'}
                </button>
              )}
            </span>
          </div>
        );
      })()}

      {/* Claim-or-Pass (CJ 2026-07-29): per-manager "pass" signals so the team
          sees who has weighed in. A pass never declines the candidate. */}
      <PassControl passes={detail.passes} currentUser={currentUser} onPass={onPass} onUnpass={onUnpass} />

      {/* Archive / restore (CJ 2026-07-29). Stale apps auto-archive after 20
          days; this lets a manager do it by hand or bring one back. */}
      <div style={{ marginTop: 8, fontSize: 12 }}>
        {a.archived_at ? (
          <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: '#8a5a1f' }}>🗄 Archived {fmtDate(a.archived_at)}.</span>
            <button onClick={onUnarchive}
              style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #d0d4d9', background: '#fff', color: '#333', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
              Restore to active
            </button>
          </span>
        ) : (
          <button onClick={onArchive}
            style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #d0d4d9', background: '#fff', color: '#777', borderRadius: 6, cursor: 'pointer' }}>
            🗄 Archive
          </button>
        )}
      </div>

      {/* Hiring v3 / Ticket C5 — informational flag badges. Display-only
          (REQ-REV-11.2). Do NOT use these to sort, hide, or auto-decide. */}
      {Array.isArray(detail.flags) && detail.flags.length > 0 && (
        <div style={{ marginTop: 10, padding: '10px 12px', background: '#fafafa',
                      border: '1px solid #e0e0e0', borderRadius: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#757575',
                        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Things to talk through · informational
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
            {detail.flags.map((f) => (
              <div key={f.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ color: '#8e6c33', flex: '0 0 auto', marginTop: 1 }}>🏷</span>
                <span>
                  <strong>{f.label}</strong>
                  {f.detail && <span style={{ color: '#666' }}> — {f.detail}</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {detail.resume_signed_url && (
        <p style={{ marginTop: 8 }}>
          <a href={detail.resume_signed_url} target="_blank" rel="noreferrer"
             style={{ fontSize: 13, color: 'var(--forest)', fontWeight: 600 }}>
            📎 Open résumé (signed link, 5-min)
          </a>
        </p>
      )}

      <hr style={{ margin: '16px 0', border: 0, borderTop: '1px solid #eee' }} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, fontSize: 12 }}>
        <div><strong>Earliest start:</strong> {a.start_date_actual ? fmtDate(a.start_date_actual) : a.start_date_band}</div>
        <div style={{ gridColumn: 'span 2' }}><strong>Tenure:</strong> {tenureLabel}</div>
        <div><strong>Commute distance:</strong> {a.commute_distance_band || '—'}</div>
        <div><strong>Reliable transport:</strong> {a.has_reliable_transport || '—'}</div>
        <div><strong>Work authorized:</strong> {a.work_authorized ? 'Yes' : 'No'}</div>
      </div>

      {a.school_breaks_text && (
        <div style={{ marginTop: 8, fontSize: 12, padding: 8, background: '#fef9ee', borderRadius: 6 }}>
          <strong>Expected breaks:</strong> {a.school_breaks_text}
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Availability</div>
        <div style={{ color: '#666', fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>{fmtAvail(a.availability)}</div>
        {a.availability_note && <div style={{ marginTop: 4, color: '#444', fontStyle: 'italic' }}>"{a.availability_note}"</div>}
      </div>

      {(a.scenario_1_response || a.scenario_2_response || a.scenario_3_response || a.scenario_4_response) && (
        <details style={{ marginTop: 16 }} open>
          <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Scenario responses</summary>
          {[1,2,3,4].map(n => a[`scenario_${n}_response`] && (() => {
            const sc = APPLY_SCENARIOS[n] || {};
            const axisKey = sc.axis;
            const axisLabel = (RUBRIC.find(([k]) => k === axisKey) || [])[1] || axisKey;
            const cur = (axisKey && axes[axisKey]) || {};
            return (
              <div key={n} style={{ marginTop: 10, fontSize: 13, color: '#333',
                                      paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
                <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                  Scenario {n} · {sc.title || ''}
                  {axisKey && <span style={{ marginLeft: 6, fontWeight: 400, textTransform: 'none', color: '#1565c0' }}>· tests {axisLabel}</span>}
                </div>
                <details style={{ marginBottom: 6 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 11, color: '#1565c0', fontWeight: 600 }}>
                    Show the scenario the candidate was answering
                  </summary>
                  <div style={{ marginTop: 4, fontSize: 12, color: '#555', background: '#fafafa',
                                  border: '1px solid #eee', padding: 8, borderRadius: 4 }}>
                    {sc.prompt}
                    {sc.why && (
                      <div style={{ marginTop: 6, fontSize: 11, color: '#777', fontStyle: 'italic' }}>
                        Why we ask: {sc.why}
                      </div>
                    )}
                  </div>
                </details>
                <div style={{ whiteSpace: 'pre-wrap', marginBottom: 6 }}>{a[`scenario_${n}_response`]}</div>
                {axisKey && canHire && !alreadyHired && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 11, color: '#555' }}>
                    <span style={{ fontWeight: 600 }}>Rate {axisLabel.toLowerCase()}:</span>
                    {RATINGS.map(r => (
                      <button key={r} type="button"
                        onClick={() => setAxisRating(axisKey, cur.rating === r ? null : r)}
                        style={RATING_BTN_STYLE(r, cur.rating === r)}>
                        {r}
                      </button>
                    ))}
                    <button type="button"
                      onClick={() => setAxisRating(axisKey, null)}
                      title="Mark as 'Not enough info'"
                      style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, fontWeight: 500,
                                border: '1px dashed #d4d4d4', cursor: 'pointer',
                                background: cur.rating == null ? '#f5f5f5' : '#fff', color: '#666' }}>
                      not enough info
                    </button>
                    <input type="text" value={cur.comment || ''}
                      onChange={(e) => setAxisComment(axisKey, e.target.value)}
                      placeholder="Optional note"
                      style={{ flex: '1 1 200px', minWidth: 0, marginLeft: 4,
                                border: '1px solid #d0d4d9', borderRadius: 6, padding: '4px 8px',
                                fontSize: 11, fontFamily: 'inherit' }} />
                  </div>
                )}
              </div>
            );
          })())}
        </details>
      )}

      {(a.reflection_hard || a.reflection_why) && (
        <details style={{ marginTop: 12 }} open>
          <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Reflections</summary>
          {a.reflection_hard && (
            <div style={{ marginTop: 8, fontSize: 13, color: '#333' }}>
              <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>What's hard for you</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{a.reflection_hard}</div>
            </div>
          )}
          {a.reflection_why && (
            <div style={{ marginTop: 8, fontSize: 13, color: '#333' }}>
              <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Why Empire</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{a.reflection_why}</div>
            </div>
          )}
        </details>
      )}

      {a.background_text && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Optional background</div>
          <div style={{ fontSize: 13, color: '#333', whiteSpace: 'pre-wrap' }}>{a.background_text}</div>
        </div>
      )}

      <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid #eee' }} />

      <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 700 }}>Decide</h3>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#757575' }}>
        Ratings live next to the content above (scenarios above, interview
        questions below). Add a comment for context, then pick a disposition.
      </p>

      {/* Scenarios already capture awareness / hospitality / collaboration /
          learning_posture inline. The three behavioral axes scenarios don't
          touch (calm under pressure, communication quality, logistics
          realism) still need a place; surfaced collapsed here so the page
          isn't dominated by a grid. CJ 2026-05-16. */}
      <details style={{ marginBottom: 12 }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#1565c0' }}>
          Other axes (calm under pressure, communication, logistics)
        </summary>
        <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
          {RUBRIC.filter(([k]) => !['awareness','hospitality','collaboration','learning_posture'].includes(k)).map(([key, label, hint]) => {
            const cur = axes[key] || {};
            return (
              <div key={key} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#212121' }}>{label}</div>
                    <div style={{ fontSize: 11, color: '#9e9e9e' }}>{hint}</div>
                  </div>
                  {RATINGS.map(r => (
                    <button key={r} type="button"
                      onClick={() => setAxisRating(key, cur.rating === r ? null : r)}
                      style={RATING_BTN_STYLE(r, cur.rating === r)}>
                      {r}
                    </button>
                  ))}
                  <button type="button"
                    onClick={() => setAxisRating(key, null)}
                    style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, fontWeight: 500,
                              border: '1px dashed #d4d4d4', cursor: 'pointer',
                              background: cur.rating == null ? '#f5f5f5' : '#fff', color: '#666' }}>
                    not enough info
                  </button>
                </div>
                <input type="text" value={cur.comment || ''}
                  onChange={(e) => setAxisComment(key, e.target.value)}
                  placeholder={`Optional note on ${label.toLowerCase()}`}
                  style={{ marginTop: 6, width: '100%', border: '1px solid #d0d4d9',
                            borderRadius: 6, padding: '6px 8px', fontSize: 12,
                            fontFamily: 'inherit', background: cur.comment ? '#fff' : '#fafafa' }} />
              </div>
            );
          })}
        </div>
      </details>

      <textarea value={comment} onChange={e => setComment(e.target.value)}
        placeholder="Comment (visible to other managers; optional)"
        rows={3}
        style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13, fontFamily: 'inherit' }} />

      <div style={{ fontSize: 11, fontWeight: 700, color: '#757575',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      marginTop: 12, marginBottom: 6 }}>
        What's next for this applicant?
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {DECISIONS.map(([id, label]) => {
          const isInvite = id === 'invite_to_interview';
          const active = decision === id;
          return (
            <button key={id} type="button"
              onClick={() => setDecision(decision === id ? '' : id)}
              style={{ fontSize: 13, padding: isInvite ? '10px 18px' : '8px 14px',
                        borderRadius: 8, fontWeight: 700,
                        border: '1px solid', cursor: 'pointer',
                        background: active ? (isInvite ? '#2e7d32' : 'var(--gold)') : '#fff',
                        borderColor: active ? (isInvite ? '#2e7d32' : 'var(--gold)') : (isInvite ? '#2e7d32' : '#d4d4d4'),
                        color: active ? '#fff' : (isInvite ? '#2e7d32' : '#444') }}>
              {isInvite ? '✉ ' : ''}{label}
            </button>
          );
        })}
      </div>
      {decision && (
        <input type="text" value={decisionReason} onChange={e => setDecisionReason(e.target.value)}
          placeholder="Decision reason (optional but encouraged)"
          style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13, marginTop: 8, fontFamily: 'inherit' }} />
      )}

      {/* Hiring v3 / Ticket C3 (extended 2026-05-08, 2026-06-15) —
          Decision email panel. Pre-filled variants per decision; send
          default ON for invite + phone-screen, OFF for pass. Pass
          templates are DB-backed (CJ 2026-06-15) so manager + owner can
          add/edit without a code change. */}
      {decision && templatesFor(decision) && (
        <div style={{ marginTop: 10, padding: 12, background: '#fafafa',
                      border: '1px solid #e0e0e0', borderRadius: 6 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={emailSend}
              onChange={e => setEmailSend(e.target.checked)} />
            <span>
              <strong>Email {greetingName} now</strong>
              {decision === 'pass' && <span style={{ color: '#666' }}> · default off — only check for a courtesy reply</span>}
              {decision !== 'pass' && <span style={{ color: '#666' }}> — sets things in motion</span>}
            </span>
          </label>
          {emailSend && (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {Object.entries(templatesFor(decision) || {}).map(([k, v]) => (
                  <label key={k} style={{ fontSize: 11, padding: '3px 10px',
                                            border: '1px solid #d0d4d9', borderRadius: 99, cursor: 'pointer',
                                            background: emailVariant === k ? 'var(--gold)' : '#fff',
                                            color: emailVariant === k ? '#fff' : '#333' }}>
                    <input type="radio" checked={emailVariant === k} style={{ display: 'none' }}
                      onChange={() => { setEmailVariant(k); applyEmailTemplate(decision, k); }} />
                    {v.label}
                  </label>
                ))}
              </div>
              {decision === 'phone_screen_set' && emailVariant === 'virtual' && (
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 2 }}>
                    Meeting link (Google Meet, Zoom, etc.) — pasted into the email
                  </label>
                  <input type="url" value={meetingLink}
                    onChange={e => setMeetingLink(e.target.value)}
                    placeholder="https://meet.google.com/abc-defg-hij"
                    style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 6,
                              fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                </div>
              )}
              <textarea value={emailBody}
                onChange={e => setEmailBody(e.target.value)}
                rows={10}
                style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 10,
                          fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
              <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                Edit before sending. Sent to <strong>{a.email}</strong>.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Context-aware action button (CJ 2026-05-20). When a disposition
          is picked and the email is set to send, the button names the
          action (e.g. "Send invite") so it's clear the click both saves
          the review row AND fires the email. */}
      {(() => {
        const willSend = !!(decision && emailSend && templatesFor(decision));
        const sendLabels = {
          invite_to_interview: '✉ Send invite',
          phone_screen_set:    '✉ Send screen invite',
          pass:                '✉ Send pass + log',
          hold_for_future:     '✓ Save hold',
        };
        const noSendLabels = {
          invite_to_interview: 'Save (no email)',
          phone_screen_set:    'Save (no email)',
          pass:                'Save (no email)',
          hold_for_future:     '✓ Save hold',
        };
        const label = willSend
          ? (sendLabels[decision] || 'Send & save')
          : (decision ? (noSendLabels[decision] || 'Save decision') : 'Save review');
        const bg = willSend ? '#2e7d32' : 'var(--gold)';
        return (
      <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={submit}
          style={{ padding: '8px 18px', background: bg, color: '#fff', border: 0,
                    borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
          {label}
        </button>
        {canHire && !alreadyHired && (
          <button onClick={() => setHireOpen(o => !o)}
            style={{ padding: '8px 18px', background: hireOpen ? '#ccc' : 'var(--forest)',
                      color: '#fff', border: 0, borderRadius: 8, fontWeight: 600,
                      cursor: 'pointer', fontSize: 13 }}>
            {hireOpen ? 'Cancel hire' : '✓ Hire this applicant'}
          </button>
        )}
        {alreadyHired && (
          <span style={{ fontSize: 12, color: '#2e7d32', fontWeight: 600 }}>✓ Already hired</span>
        )}
        {alreadyHired && onUnhire && window.EmpireRoles.isOwner(currentUser) && (
          <button onClick={onUnhire}
            title="Reverse the hire: reset application to under-review, delete user, free the email. Owner-only, for testing."
            style={{ padding: '6px 14px', background: '#fff', color: '#991b1b',
                      border: '1px solid #fecaca', borderRadius: 6, fontWeight: 600,
                      cursor: 'pointer', fontSize: 12 }}>
            Unhire
          </button>
        )}
        {savingMsg && <span style={{ fontSize: 12, color: '#666' }}>{savingMsg}</span>}
      </div>
        );
      })()}

      {hireOpen && canHire && !alreadyHired && (
        <div style={{ marginTop: 12, padding: 14, background: '#f0f9f0', border: '1px solid #c8e6c9', borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: '#2e7d32' }}>
            Bring {a.first_name} {a.last_name} on as Monarch staff
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 2 }}>Primary location</label>
              <select value={hireForm.primary_location_id}
                onChange={e => setHireForm(f => ({ ...f, primary_location_id: e.target.value }))}
                style={{ width: '100%', padding: 6, border: '1px solid #d0d4d9', borderRadius: 6, fontSize: 13 }}>
                <option value="">— pick —</option>
                {(locations || []).map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 2 }}>Role</label>
              <select value={hireForm.role}
                onChange={e => setHireForm(f => ({ ...f, role: e.target.value }))}
                style={{ width: '100%', padding: 6, border: '1px solid #d0d4d9', borderRadius: 6, fontSize: 13 }}>
                <option>Barista</option>
                <option>Shift Lead</option>
                <option>Kitchen</option>
                <option>Asst Manager</option>
                <option>Manager</option>
                <option>Coffee Roaster</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 2 }}>Hourly rate ($/hr)</label>
              <input type="number" min="0" step="0.25" value={hireForm.hourly_rate}
                onChange={e => setHireForm(f => ({ ...f, hourly_rate: e.target.value }))}
                style={{ width: '100%', padding: 6, border: '1px solid #d0d4d9', borderRadius: 6, fontSize: 13 }}
                placeholder="17.00" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 2 }}>Pay entity</label>
              <select value={hireForm.pay_entity}
                onChange={e => setHireForm(f => ({ ...f, pay_entity: e.target.value }))}
                style={{ width: '100%', padding: 6, border: '1px solid #d0d4d9', borderRadius: 6, fontSize: 13 }}>
                <option value="ETC">ETC (Empire Tea & Coffee)</option>
                <option value="NBT">NBT (Newport Bagel Trade)</option>
              </select>
            </div>
          </div>
          {/* Hiring v3 / Ticket F1 — Additional locations multi-select */}
          {(locations || []).length > 1 && (
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>
                Additional locations (cross-trained)
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(locations || [])
                  .filter(l => Number(l.id) !== Number(hireForm.primary_location_id))
                  .map(l => {
                    const sel = hireForm.additional_location_ids.includes(l.id);
                    return (
                      <label key={l.id} style={{ fontSize: 11, padding: '3px 10px',
                                                  border: '1px solid #d0d4d9', borderRadius: 99, cursor: 'pointer',
                                                  background: sel ? 'var(--gold)' : '#fff',
                                                  color: sel ? '#fff' : '#333' }}>
                        <input type="checkbox" checked={sel} style={{ display: 'none' }}
                          onChange={() => setHireForm(f => ({
                            ...f,
                            additional_location_ids: sel
                              ? f.additional_location_ids.filter(x => x !== l.id)
                              : [...f.additional_location_ids, l.id],
                          }))} />
                        {l.name}
                      </label>
                    );
                  })}
              </div>
            </div>
          )}
          {/* First-shift date. Mentor section removed per CJ 2026-05-16 —
              managers assign mentors later via the Mentor-needed dashboard. */}
          <div style={{ marginTop: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 2 }}>
                First shift date (optional)
              </label>
              <input type="date" value={hireForm.first_shift_date}
                onChange={e => setHireForm(f => ({ ...f, first_shift_date: e.target.value }))}
                style={{ width: '100%', padding: 6, border: '1px solid #d0d4d9', borderRadius: 6, fontSize: 13 }} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12, color: '#444' }}>
            <input type="checkbox" checked={!!hireForm.send_welcome}
              onChange={e => setHireForm(f => ({ ...f, send_welcome: e.target.checked }))} />
            Send "set your password" welcome email to {a.email}
          </label>
          <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={submitHire} disabled={hireDisabled}
              style={{ padding: '8px 18px',
                        background: hireDisabled ? '#9ca99e' : '#2e7d32',
                        color: '#fff', border: 0,
                        borderRadius: 8, fontWeight: 600,
                        cursor: hireDisabled ? 'not-allowed' : 'pointer', fontSize: 13 }}>
              {hireSaving ? 'Hiring…' : 'Confirm hire'}
            </button>
            <button onClick={() => setHireOpen(false)}
              style={{ padding: '8px 16px', background: '#fff', color: '#444',
                        border: '1px solid #d0d4d9', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
            {hireDisabled && !hireSaving && (
              <span style={{ fontSize: 11, color: '#b00020' }}>
                {!(Number(hireForm.primary_location_id) > 0)
                  ? 'Pick a primary location.'
                  : 'Enter an hourly rate.'}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Hiring v3 / Ticket D2 — Interview question picker. Lazy-loaded.
          Visible to all interested-location managers (cross-visibility per
          §12.4). Picks come from the active question pool managed in
          Settings → Hiring → Interview questions. */}
      <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid #eee' }} />
      <InterviewScriptPanel applicationId={a.id} headers={{ 'x-user-id': String(currentUser.id) }} applicantRole={
        Array.isArray(a.role_interests) && a.role_interests.length > 0 ? a.role_interests[0] : null
      } />

      {reviews.length > 0 && (
        <>
          <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid #eee' }} />
          <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 700 }}>Activity ({reviews.length})</h3>
          {reviews.map(r => (
            <div key={r.id} style={{ padding: 10, borderTop: '1px solid #f3f4f6', fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9e9e9e' }}>
                <span><strong>{r.reviewer_name || 'Manager'}</strong></span>
                <span>{new Date(r.created_at).toLocaleString()}</span>
              </div>
              {r.comment && <div style={{ marginTop: 4, color: '#333' }}>{r.comment}</div>}
              {(() => {
                // Hiring v3 / Ticket C1 — render the 7-axis review using
                // per-axis rows when available (new shape), falling back to
                // the legacy 6 columns for historical reviews. Each axis
                // shows rating + optional per-axis comment.
                // r.axes is keyed by the new axis names; legacy r.rating_*
                // columns map via LEGACY_TO_NEW.
                const LEGACY_TO_NEW = {
                  rating_awareness: 'awareness',
                  rating_humane_treatment: 'hospitality',
                  rating_collaborative: 'collaboration',
                  rating_learning_posture: 'learning_posture',
                  rating_specificity: 'communication_quality',
                  rating_logistics_realism: 'logistics_realism',
                };
                const merged = {};
                for (const [legCol, newAxis] of Object.entries(LEGACY_TO_NEW)) {
                  if (r[legCol]) merged[newAxis] = { rating: r[legCol], comment: null };
                }
                if (r.axes) {
                  for (const [axis, val] of Object.entries(r.axes)) {
                    if (val && (val.rating || val.comment)) merged[axis] = val;
                  }
                }
                const rendered = RUBRIC.filter(([k]) => merged[k]);
                if (rendered.length === 0) return null;
                return (
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', fontSize: 11 }}>
                    {rendered.map(([k, label]) => {
                      const m = merged[k];
                      const r = m.rating;
                      const bg = r === 'strong' ? '#dcfce7' : r === 'ok' ? '#fef3c7' : r === 'weak' ? '#fee2e2' : '#f5f5f5';
                      const fg = r === 'strong' ? '#166534' : r === 'ok' ? '#92400e' : r === 'weak' ? '#991b1b' : '#444';
                      return (
                        <span key={k} title={m.comment || ''}
                          style={{ background: bg, color: fg, padding: '2px 8px', borderRadius: 4 }}>
                          {label}{r ? <>: <strong>{r}</strong></> : ' (note)'}
                          {m.comment && <span style={{ opacity: 0.7 }}> ✎</span>}
                        </span>
                      );
                    })}
                  </div>
                );
              })()}
              {r.decision && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#212121' }}>
                  Decision: <strong>{r.decision.replace(/_/g, ' ')}</strong>
                  {r.decision_reason && <span style={{ color: '#666' }}> — {r.decision_reason}</span>}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
